import { AxiosError } from 'axios';
import { get, isArray, isObject, mapValues, omitBy, reduce } from 'lodash';
import { Schema } from 'normalizr';
import { OutputSelector } from 'reselect';

import { PayloadApiSuccess } from '../actions/sagaApi';

import createResourceCommonActions from './createResourceCommonActions';
import createResourceCommonLookups, { CreateResourceLookupsArgs, Lookups } from './createResourceCommonLookups';
import createResourceSchema, { CreateResourceSchemaArgs } from './createResourceSchema';
import createResourceSelectors, {
  CreateResourceSelectorsArgs,
  EntitiesObject,
  ResourceSelectors,
} from './createResourceSelectors';
import { EntityBase } from './types';

export interface CreateResourceArgs<E extends EntityBase>
  extends Partial<CreateResourceLookupsArgs>,
    Partial<CreateResourceSchemaArgs<E>>,
    Partial<CreateResourceSelectorsArgs> {
  key: string;
  url: string;
  hasFetchBulkSupport?: boolean;
  fetchPageUrl?: string;
  fetchListUrl?: string;
  createFetchSchema?: (schema: Schema) => Schema;
  createFetchListSchema?: (schema: Schema) => Schema;
  createFetchPageSchema?: (schema: Schema) => Schema;
  createRemoveSchema?: (schema: Schema) => Schema;
  saveMessageSuccess?: string | ((response?: PayloadApiSuccess<E>) => string);
  saveMessageError?: string | ((error?: AxiosError) => string);
  saveRequestInterceptor?: (data: E) => any;
  removeMessageSuccess?: string | ((response?: PayloadApiSuccess<E>) => string);
  removeMessageError?: string | ((error?: AxiosError) => string);
}

export type OptionalResource<P> = Omit<P, 'resource'>;

export interface ResourceLite<E extends EntityBase> extends ResourceSelectors {
  fetchListUrl: string;
  fetchPageUrl: string;
  fetchBulkUrl?: string;
  idAttribute: CreateResourceSchemaArgs<E>['idAttribute'];
  initialState: any;
  key: string;
  lookups?: Lookups;
  saveRequestInterceptor: (data: E) => any;
  url: string;

  // Schemas
  fetchListSchema: Schema;
  fetchPageSchema: Schema;
  fetchBulkSchema?: Schema;
  fetchSchema: Schema;
  removeSchema: Schema;
  schema: Schema;

  // Messages
  removeMessageError: CreateResourceArgs<E>['removeMessageError'];
  removeMessageSuccess: CreateResourceArgs<E>['removeMessageSuccess'];
  saveMessageError: CreateResourceArgs<E>['saveMessageError'];
  saveMessageSuccess: CreateResourceArgs<E>['saveMessageSuccess'];
}

export interface Resource<E extends EntityBase> extends ResourceLite<E> {
  // Selector required to fetch lookups
  selectAllWithLookups: OutputSelector<
    any,
    EntitiesObject<E>,
    (res1: EntitiesObject<E>, res2: any) => EntitiesObject<E>
  >;
}

export const omitExtendedFields = (value?: any) => {
  if (!isObject(value) || isArray(value)) {
    return value;
  }
  const res: any = mapValues(value, omitExtendedFields);
  return omitBy(res, (_: any, k: string) => k.indexOf('$') === 0);
};

const createResource = <E extends EntityBase>(args: CreateResourceArgs<E>) => {
  const {
    url,
    schemaDefinition,
    key,
    idAttribute,
    lookups,
    processStrategy,
    selectLookups,
    hasFetchBulkSupport,
    fetchPageUrl,
    fetchListUrl,
    createFetchSchema,
    createFetchListSchema,
    createFetchPageSchema,
    createRemoveSchema,
    saveMessageSuccess,
    saveMessageError,
    saveRequestInterceptor = (data: E) => data,
    removeMessageSuccess,
    removeMessageError,
  } = args;

  const fromLookups = createResourceCommonLookups({ lookups });

  const schema = createResourceSchema<E>({
    key,
    idAttribute,
    processStrategy,
    schemaDefinition: schemaDefinition || fromLookups.schemaDefinition,
  });

  const postSaveRequestInterceptor = (data: E) => {
    const result = omitExtendedFields(saveRequestInterceptor(data));

    // Remove lookups
    return reduce(
      result,
      (obj, value, lookupKey) => ({
        ...obj,
        [lookupKey]: lookups && lookups[lookupKey] && isObject(value) ? get(value, 'id') : value,
      }),
      {},
    );
  };

  const resource = {
    key,
    url,
    idAttribute,
    initialState: {},
    schema,
    lookups,
    fetchListSchema: createFetchListSchema ? createFetchListSchema(schema) : { response: [schema] },
    fetchListUrl: fetchListUrl || url,
    fetchBulkUrl: hasFetchBulkSupport ? `${url}/crudList` : undefined,
    fetchBulkSchema: hasFetchBulkSupport ? { response: [schema] } : undefined,
    fetchPageSchema: createFetchPageSchema ? createFetchPageSchema(schema) : { response: [schema] },
    fetchPageUrl: fetchPageUrl || `${url}/search`,
    fetchSchema: createFetchSchema ? createFetchSchema(schema) : schema,
    removeMessageError: removeMessageError || 'Error deleting item',
    removeMessageSuccess: removeMessageSuccess || 'Item deleted!',
    removeSchema: createRemoveSchema ? createRemoveSchema(schema) : schema,
    saveRequestInterceptor: (data: E) => postSaveRequestInterceptor(saveRequestInterceptor(data)),
    saveMessageError: saveMessageError || 'Error saving item',
    saveMessageSuccess: saveMessageSuccess || 'Item saved!',

    // Common selectors for all resources
    ...createResourceSelectors<E>({ key, selectLookups: selectLookups || fromLookups.selectLookups }),
  };

  return {
    ...resource,

    // Common actions for all resources
    ...createResourceCommonActions<E>(resource),
  };
};

export default createResource;
